import { Button, Grid } from "antd";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";
import i18n from "src/i18n";
import { DownIcon, CorrectIcon } from "src/assets/Icons";
import { HotelRoom } from "src/store/apis/Hotels";
import { formatNumber } from "src/utility";
import { MdRestaurantMenu } from "react-icons/md";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { useDirection } from "src/i18n/direction";
import {
  getMealTypeLabel,
  splitName,
  getRoomBlocks,
  getInclusionTags,
  getCancellationInfo,
  formatSupplementDescription,
} from "src/Modules/Hotels/utils/room";

function getRoomTitle(room: HotelRoom, totalAdults?: number, totalChildren?: number): string {
  const rawCount = room.roomTypeName?.length || 1;
  const adults = totalAdults ?? 1;
  const children = totalChildren ?? 0;
  const roomsLabel = rawCount === 1 ? i18n.t("Hotels.booking.oneRoom") : i18n.t("Hotels.booking.roomsCount", { count: rawCount });
  const adultsLabel = adults === 1 ? i18n.t("Hotels.common.oneAdult") : i18n.t("Hotels.common.adultsCount", { count: adults });
  const childrenLabel = children === 1 ? i18n.t("Hotels.common.oneChild") : i18n.t("Hotels.common.childrenCount", { count: children });
  return `${roomsLabel} — ${adultsLabel}, ${childrenLabel}`;
}

interface AccommodationsListProps {
  rooms: HotelRoom[];
  currency: string | null;
  originalCurrency?: string | null;
  numberOfNights: number;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onProceed?: (index: number) => void;
  pointsFactor: number;
  hotelImage?: string | null;
  totalAdults?: number;
  totalChildren?: number;
}

const AccommodationCard: FC<{
  room: HotelRoom;
  index: number;
  currency: string | null;
  originalCurrency?: string | null;
  numberOfNights: number;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (index: number) => void;
  onProceed?: (index: number) => void;
  pointsFactor: number;
  totalAdults?: number;
  totalChildren?: number;
}> = ({
  room,
  index,
  currency,
  originalCurrency,
  numberOfNights,
  isSelected,
  isDisabled,
  onSelect,
  onProceed,
  pointsFactor,
  totalAdults,
  totalChildren,
}) => {
  const { t } = useTranslation();
  const pricePerNight = numberOfNights > 0 ? Math.round(room.totalFare / numberOfNights) : room.totalFare;
  const currencySymbol = currency ?? "$";
  const blocks = getRoomBlocks(room);
  const cancellation = getCancellationInfo(room.cancelPolicies, room.isRefundable);
  const inclusionTags = getInclusionTags(room);

  return (
    <div className="border-s-border-primary shadow-card w-[92%] shrink-0 snap-start rounded-lg border-s-4 bg-background-default p-6 transition-all md:w-auto md:shrink">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
        {/* Left Side: Room Info */}
        <div className="flex-1">
          <h4 className="mb-8 text-xl font-bold text-font-default">{getRoomTitle(room, totalAdults, totalChildren)}</h4>

          {blocks.map((block, i) => {
            const { title, details } = splitName(block.name);
            const displayTitle = block.count > 1 ? Array(block.count).fill(title).join(" & ") : title;
            return (
              <div key={i} className="mb-5 flex flex-col gap-3 border-s-2 border-s-border-hover ps-4">
                <div className="leading-0 flex flex-col justify-center">
                  <h5 className="text-font-default m-0 text-[16px] font-bold leading-6">{displayTitle}</h5>
                </div>

                {details && (
                  <div className="text-12 leading-0 flex flex-col justify-center text-left text-font-subtle">
                    <p className="leading-5.5 m-0 font-bold">{t("Hotels.common.roomDetails")}</p>
                    <p className="leading-5.5 m-0 font-normal">{details}</p>
                  </div>
                )}

                {block.promotion && (
                  <div className="text-12 leading-0 flex flex-col justify-center text-left text-font-subtle">
                    <p className="leading-5.5 m-0 font-bold">{t("Hotels.booking.promotion")}</p>
                    <p className="leading-5.5 m-0 font-normal">{block.promotion}</p>
                  </div>
                )}

                {inclusionTags.length > 0 && (
                  <div className="text-12 leading-0 flex flex-col justify-center text-left text-font-subtle">
                    <p className="leading-5.5 m-0 font-bold">{t("Hotels.booking.inclusions")}</p>
                    <p className="leading-5.5 m-0 font-normal">{inclusionTags.join(" • ")}</p>
                  </div>
                )}

                <div className="mt-1 flex flex-wrap gap-2 text-xs font-bold">
                  <span
                    className={`inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5 ${
                      cancellation.refundable
                        ? "bg-background-success text-font-success"
                        : "bg-background-danger text-font-danger"
                    }`}
                  >
                    {cancellation.refundable ? t("Flights.listing.refundable") : t("Hotels.common.nonRefundable")}
                  </span>

                  {cancellation.hasFreeCancellation && (
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-success px-3 py-1.5 text-font-success">
                      <CorrectIcon className="text-font-success h-4 w-4" />
                      {cancellation.freeCancelUntil
                        ? t("Hotels.common.freeCancellationUntil", { date: cancellation.freeCancelUntil })
                        : t("Hotels.common.freeCancellation")}
                    </span>
                  )}

                  {cancellation.paidPolicies.map((policy, pi) => (
                    <span key={pi} className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-danger px-3 py-1.5 text-font-danger">
                      {t(
                        policy.chargeType === "Percentage"
                          ? "Hotels.common.cancellationChargePercentage"
                          : "Hotels.common.cancellationChargeAmount",
                        {
                          date: policy.fromDate,
                          charge:
                            policy.chargeType === "Percentage" ? policy.cancellationCharge : formatNumber(policy.cancellationCharge),
                          currency: policy.currency ?? currency,
                        },
                      )}
                    </span>
                  ))}

                  {room.mealType && (
                    <span className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5">
                      <MdRestaurantMenu className="h-4 w-4" />
                      {getMealTypeLabel(room.mealType)}
                    </span>
                  )}

                  {inclusionTags.length > 0 &&
                    inclusionTags.map((tag, ti) => (
                      <span
                        key={ti}
                        className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5"
                      >
                        <CorrectIcon className="h-4 w-4 shrink-0" />
                        {tag}
                      </span>
                    ))}

                  {block.promotion && (
                    <span className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5">
                      <CorrectIcon className="h-4 w-4 shrink-0" />
                      {block.promotion}
                    </span>
                  )}

                  {block.mandatorySupplements.length > 0 && (
                    <span className="text-font-warning bg-background-warning inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
                      {block.mandatorySupplements
                        .map(
                          (s) =>
                            `${formatSupplementDescription(s.description)}${s.price > 0 ? ` - ${t("Hotels.common.payAtHotel", { price: formatNumber(s.price), currency: s.currency ?? "" })}` : ""}`,
                        )
                        .join(" · ")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Side: Price */}
        <div className="hidden shrink-0 flex-col items-end pt-1 md:flex">
          <p className="text-font-active text-3xl font-bold tracking-tight">
            {formatNumber(pricePerNight)} {currencySymbol}
          </p>
          <p className="mt-0.5 text-xs font-medium text-font-subtle">{t("Hotels.common.perNight")}</p>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="mt-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium text-font-subtle">{t("Hotels.booking.stayTotal")}</div>

          <div className="flex items-center gap-4">
            <div className="text-font-default text-lg font-bold">
              {formatNumber(room.totalFare)} {currencySymbol}
            </div>

            {room.totalOriginalFare != null && (
              <>
                <div className="text-border-default">|</div>
                <div className="text-font-default text-lg font-bold">
                  {formatNumber(room.totalOriginalFare)} {originalCurrency}
                </div>
              </>
            )}

            <div className="text-border-default">|</div>
            <div className="text-font-active text-lg font-bold">
              {formatNumber(Math.round(room.totalFare * pointsFactor))} {t("Flights.listing.pts")}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <Button
            type="primary"
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) {
                onSelect(index);
                onProceed?.(index);
              }
            }}
            className="h-10! rounded-sm! border-transparent! bg-background-primary! text-font-inverted! px-8! text-sm! font-bold! shadow-none! w-full md:w-auto"
          >
            {t("Hotels.booking.bookRoom")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AccommodationsList: FC<AccommodationsListProps> = ({
  rooms,
  currency,
  originalCurrency,
  numberOfNights,
  selectedIndex,
  onSelect,
  onProceed,
  pointsFactor,
  totalAdults,
  totalChildren,
}) => {
  const { t } = useTranslation();
  const [sortAsc, setSortAsc] = useState<boolean | null>(null);
  const isLargeScreen = Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const direction = useDirection();
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    axis: "x",
    align: "start",
    dragThreshold: 6,
    inViewThreshold: 0.1,
    active: !isLargeScreen,
    direction,
    dragFree: true,
  });

  const displayedRooms = useMemo(() => {
    const indexed = rooms.map((room, originalIndex) => ({ room, originalIndex }));
    if (sortAsc === null) return indexed;
    return indexed.sort((a, b) => (sortAsc ? a.room.totalFare - b.room.totalFare : b.room.totalFare - a.room.totalFare));
  }, [rooms, sortAsc]);

  const toggleSort = () => {
    setSortAsc((prev) => (prev === null ? true : !prev));
  };

  if (!rooms.length) {
    return (
      <div className="rounded-md border border-border-default bg-background-subtle p-6 text-center text-font-subtle">
        {t("Hotels.booking.noRoomsAvailable")}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-font-default">{t("Hotels.booking.availableAccommodations")}</h3>
        {rooms.length > 1 && (
          <button
            type="button"
            onClick={toggleSort}
            className="flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium text-font-subtle hover:bg-background-hover"
          >
            <DownIcon className="h-4 w-4" transform={sortAsc ? "scale(1,-1)" : ""} />
            <div className="whitespace-nowrap">{t("Hotels.booking.sortByPrice")}</div>
          </button>
        )}
      </div>
      <div ref={emblaRef} className="overflow-hidden md:overflow-visible">
        <div className="flex flex-nowrap gap-4 md:flex-col md:gap-0 md:space-y-6">
          {displayedRooms.map(({ room, originalIndex }) => (
            <AccommodationCard
              key={originalIndex}
              room={room}
              index={originalIndex}
              currency={currency}
              originalCurrency={originalCurrency}
              numberOfNights={numberOfNights}
              isSelected={selectedIndex === originalIndex}
              isDisabled={room.bookingCode === null}
              onSelect={onSelect}
              onProceed={onProceed}
              pointsFactor={pointsFactor}
              totalAdults={totalAdults}
              totalChildren={totalChildren}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccommodationsList;
